// ─── GenericDiffTable ────────────────────────────────────────────────────────
//
// Fallback diff renderer for entity types that don't have a dedicated
// per-entity conflict view. Flattens both client and server versions and
// renders a tri-state color-coded comparison table.

import { cn } from '../../../constants/design-tokens';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function flattenObject(
  obj: Record<string, unknown>,
  prefix = '',
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      Object.assign(result, flattenObject(value as Record<string, unknown>, fullKey));
    } else {
      result[fullKey] = value;
    }
  }
  return result;
}

function diffObjects(
  client: Record<string, unknown>,
  server: Record<string, unknown>,
): { changed: string[]; clientOnly: string[]; serverOnly: string[] } {
  const flatClient = flattenObject(client);
  const flatServer = flattenObject(server);
  const clientKeys = new Set(Object.keys(flatClient));
  const serverKeys = new Set(Object.keys(flatServer));

  const changed: string[] = [];
  const clientOnly: string[] = [];
  const serverOnly: string[] = [];

  for (const key of clientKeys) {
    if (!serverKeys.has(key)) {
      clientOnly.push(key);
    } else if (JSON.stringify(flatClient[key]) !== JSON.stringify(flatServer[key])) {
      changed.push(key);
    }
  }
  for (const key of serverKeys) {
    if (!clientKeys.has(key)) {
      serverOnly.push(key);
    }
  }

  return { changed, clientOnly, serverOnly };
}

function formatDiffValue(value: unknown): string {
  if (value === null || value === undefined) return '\u2014';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function truncate(str: string, maxLen: number): string {
  return str.length > maxLen ? `${str.slice(0, maxLen)}\u2026` : str;
}

// ─── Component ──────────────────────────────────────────────────────────────

export interface GenericDiffTableProps {
  clientVersion: Record<string, unknown>;
  serverVersion: Record<string, unknown>;
}

export function GenericDiffTable({
  clientVersion,
  serverVersion,
}: GenericDiffTableProps) {
  const flatClient = flattenObject(clientVersion);
  const flatServer = flattenObject(serverVersion);
  const { changed, clientOnly, serverOnly } = diffObjects(clientVersion, serverVersion);
  const allKeys = Array.from(new Set([...Object.keys(flatClient), ...Object.keys(flatServer)])).sort();

  return (
    <div className="overflow-x-auto rounded-xl">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-[10px] text-neutral-400 dark:text-neutral-500 uppercase tracking-wider">
            <th className="text-left px-3 py-2 font-medium w-1/4">Field</th>
            <th className="text-left px-3 py-2 font-medium w-[37.5%]">Client</th>
            <th className="text-left px-3 py-2 font-medium w-[37.5%]">Server</th>
          </tr>
        </thead>
        <tbody>
          {allKeys.map((key) => {
            const isChanged = changed.includes(key);
            const isClientOnly = clientOnly.includes(key);
            const isServerOnly = serverOnly.includes(key);

            let rowBg = '';
            if (isChanged) rowBg = 'bg-warning-50/50 dark:bg-warning-900/10';
            else if (isClientOnly) rowBg = 'bg-success-50/50 dark:bg-success-900/10';
            else if (isServerOnly) rowBg = 'bg-sky-50/50 dark:bg-sky-900/10';

            return (
              <tr key={key} className={cn('transition-colors', rowBg)}>
                <td className="px-3 py-1.5 font-mono text-neutral-700 dark:text-neutral-300 break-all">
                  {key}
                  {isChanged && <span className="ml-1 text-warning-500">{'\u25CF'}</span>}
                  {isClientOnly && <span className="ml-1 text-success-500 text-[9px]">+client</span>}
                  {isServerOnly && <span className="ml-1 text-sky-500 text-[9px]">+server</span>}
                </td>
                <td className="px-3 py-1.5 font-mono text-neutral-600 dark:text-neutral-400 break-all">
                  {truncate(formatDiffValue(flatClient[key]), 80)}
                </td>
                <td className="px-3 py-1.5 font-mono text-neutral-600 dark:text-neutral-400 break-all">
                  {truncate(formatDiffValue(flatServer[key]), 80)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
